import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { 
  ALL_AVAILABLE_SUBJECT_IDS, 
  SUBJECT_NAMES, 
  getSubjectClass, 
  getSubjectName, 
  formatSubject 
} from '../../utils/constants';

const TeacherProgress = () => {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const assigned = userProfile?.assignedSubjects || [];
  const subjects = (isAdmin || assigned.length === 0) ? ALL_AVAILABLE_SUBJECT_IDS : assigned;
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debugMsg, setDebugMsg] = useState('Starting...');
  
  // Filters and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      setDebugMsg('Initializing...');
      if (subjects.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setDebugMsg('Fetching subject lessons...');
        const subjectLessonsMap = {};
        subjects.forEach(s => { subjectLessonsMap[s] = []; });

        const lessonsQuery = query(
          collection(db, 'content_approvals'),
          where('status', '==', 'approved')
        );
        const snap = await getDocs(lessonsQuery);
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.type?.toLowerCase() === 'lesson' && data.subjectId && subjectLessonsMap[data.subjectId]) {
            subjectLessonsMap[data.subjectId].push(d.id);
          }
        });

        setDebugMsg('Fetching students list...');
        const studentsQuery = query(collection(db, 'users'), where('role', '==', 'student'));
        const studentsSnap = await getDocs(studentsQuery);
        
        const roster = [];
        const chunkSize = 20;
        for (let i = 0; i < studentsSnap.docs.length; i += chunkSize) {
          setDebugMsg(`Processing students ${i + 1} to ${Math.min(i + chunkSize, studentsSnap.docs.length)}...`);
          const chunk = studentsSnap.docs.slice(i, i + chunkSize);
          
          const chunkPromises = chunk.map(async (studentDoc) => {
            const studentData = studentDoc.data();
            const studentId = studentDoc.id;
            
            try {
              const [subSnap, progressDocSnap] = await Promise.all([
                getDocs(collection(db, 'subscriptions', studentId, 'subjects')),
                getDoc(doc(db, 'student_progress', studentId))
              ]);
              
              let isClassPackageActive = false;
              const individualActiveSubjects = new Set();
              const subDocsMap = {};
              
              subSnap.forEach((subDoc) => {
                const subData = subDoc.data();
                if (subDoc.id === '_class_package' && subData.classPackageActive === true) {
                  isClassPackageActive = true;
                } else if (subData.status === 'active') {
                  individualActiveSubjects.add(subDoc.id);
                }
                subDocsMap[subDoc.id] = subData;
              });

              let completedLessonIds = [];
              if (progressDocSnap.exists()) {
                completedLessonIds = progressDocSnap.data().completedLessonIds || [];
              }
              
              let progressSum = 0;
              let matchedSubjectsCount = 0;
              const studentSubjects = [];
              
              for (const subjectId of subjects) {
                const subjectClass = getSubjectClass(subjectId);
                const studentClass = studentData.classLevel?.toLowerCase();
                
                const isSubbed = individualActiveSubjects.has(subjectId) || 
                  (isClassPackageActive && studentClass === subjectClass);
                  
                if (isSubbed) {
                  const approvedLessonIds = subjectLessonsMap[subjectId] || [];
                  const totalLessons = approvedLessonIds.length;
                  
                  let subjectProgress = 0;
                  let completedCount = 0;
                  if (totalLessons > 0) {
                    completedCount = approvedLessonIds.filter(id => completedLessonIds.includes(id)).length;
                    subjectProgress = Math.round((completedCount / totalLessons) * 100);
                  } else {
                    if (subDocsMap[subjectId]) {
                      subjectProgress = subDocsMap[subjectId].progress || 0;
                    }
                  }
                  
                  progressSum += subjectProgress;
                  matchedSubjectsCount += 1;
                  studentSubjects.push({
                    subjectId,
                    subjectName: formatSubject(subjectId),
                    progress: subjectProgress,
                    completedLessons: completedCount,
                    totalLessons: totalLessons
                  });
                }
              }
              
              if (matchedSubjectsCount > 0) {
                const avgProgress = Math.round(progressSum / matchedSubjectsCount);
                
                let status = 'Average';
                if (avgProgress >= 80) status = 'Top Performer';
                else if (avgProgress >= 60) status = 'Excellent';
                else if (avgProgress >= 40) status = 'Good';
                else if (avgProgress > 0) status = 'In Progress';
                else status = 'Needs Attention';

                return {
                  id: studentId,
                  name: studentData.fullName || 'Unknown Student',
                  email: studentData.email || '',
                  classLevel: studentData.classLevel || 'General',
                  progress: avgProgress,
                  status: status,
                  subjects: studentSubjects
                };
              }
            } catch (studentErr) {
              console.error(`Error loading data for student ${studentId}:`, studentErr);
            }
            return null;
          });
          
          const results = await Promise.all(chunkPromises);
          results.forEach(res => {
            if (res) roster.push(res);
          });
        }
        
        setStudents(roster);
      } catch (err) {
        console.error("Error fetching progress:", err);
        setDebugMsg(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [userProfile]);

  // Overall statistics for top summary
  const totalStudents = students.length;
  const avgProgressOverall = totalStudents > 0 
    ? Math.round(students.reduce((acc, s) => acc + s.progress, 0) / totalStudents) 
    : 0;
  const topPerformersCount = students.filter(s => s.progress >= 80).length;
  const needsAttentionCount = students.filter(s => s.progress < 40).length;

  // Filter students
  const filteredStudents = students.filter((s) => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          s.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesClass = selectedClass === 'all' || 
                         s.classLevel.toLowerCase() === selectedClass.toLowerCase();

    let matchesStatus = true;
    if (selectedStatus === 'top') matchesStatus = s.progress >= 80;
    else if (selectedStatus === 'active') matchesStatus = s.progress > 0 && s.progress < 80;
    else if (selectedStatus === 'needs_attention') matchesStatus = s.progress < 40;

    return matchesSearch && matchesClass && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Top Performer':
        return 'bg-green-100 text-green-900 border-green-300';
      case 'Excellent':
        return 'bg-teal-100 text-teal-900 border-teal-300';
      case 'Good':
        return 'bg-blue-100 text-blue-900 border-blue-300';
      case 'In Progress':
        return 'bg-indigo-100 text-indigo-900 border-indigo-300';
      case 'Needs Attention':
      default:
        return 'bg-rose-100 text-rose-900 border-rose-300';
    }
  };

  const getProgressBarColor = (progress) => {
    if (progress >= 80) return 'bg-gradient-to-r from-emerald-500 to-green-600';
    if (progress >= 50) return 'bg-gradient-to-r from-blue-500 to-indigo-600';
    if (progress >= 20) return 'bg-gradient-to-r from-amber-400 to-amber-600';
    return 'bg-gradient-to-r from-rose-400 to-rose-600';
  };

  const classTabs = [
    { id: 'all', label: 'All Classes' },
    { id: 'ss1', label: 'SS1' },
    { id: 'ss2', label: 'SS2' },
    { id: 'ss3', label: 'SS3' },
    { id: 'utme', label: 'UTME' },
  ];

  return (
    <div className="max-w-7xl mx-auto w-full py-4 md:py-6">
      
      {/* Page Header (No icons, pure human typography) */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-extrabold text-darkNavy mb-2 font-poppins tracking-tight">
          Student Progress Overview
        </h1>
        <p className="text-sm md:text-base text-gray-500">
          Monitor your students' real-time lesson completions, active subjects, and learning consistency.
        </p>
      </div>

      {/* Top Summary KPI Cards (No icons, high readability) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-xs">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
            Total Students
          </p>
          <p className="text-2xl md:text-3xl font-black text-darkNavy">
            {totalStudents}
          </p>
          <p className="text-xs text-gray-400 mt-1 font-medium">
            Enrolled in your subjects
          </p>
        </div>

        <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-xs">
          <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-1">
            Average Progress
          </p>
          <p className="text-2xl md:text-3xl font-black text-blue-700">
            {avgProgressOverall}%
          </p>
          <p className="text-xs text-gray-400 mt-1 font-medium">
            Overall syllabus completion
          </p>
        </div>

        <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-xs">
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">
            Top Performers
          </p>
          <p className="text-2xl md:text-3xl font-black text-emerald-700">
            {topPerformersCount}
          </p>
          <p className="text-xs text-gray-400 mt-1 font-medium">
            80% or higher completion
          </p>
        </div>

        <div className="bg-white p-4 md:p-5 rounded-2xl border border-gray-200 shadow-xs">
          <p className="text-xs font-bold text-rose-700 uppercase tracking-wider mb-1">
            Needs Attention
          </p>
          <p className="text-2xl md:text-3xl font-black text-rose-700">
            {needsAttentionCount}
          </p>
          <p className="text-xs text-gray-400 mt-1 font-medium">
            Less than 40% completion
          </p>
        </div>
      </div>

      {/* Class Level Pill Tabs (Consistent with Subscriptions & Content Approval) */}
      <div className="flex flex-wrap items-center gap-2 mb-4 md:mb-5">
        {classTabs.map((tab) => {
          const isActive = selectedClass === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setSelectedClass(tab.id)}
              className={`px-4 py-2 rounded-xl text-xs md:text-sm font-bold border transition-all cursor-pointer whitespace-nowrap ${
                isActive
                  ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
                  : 'bg-indigo-50 text-indigo-900 border-indigo-200 hover:bg-indigo-100'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search and Performance Filter Row */}
      <div className="bg-white p-3 md:p-4 rounded-2xl border border-gray-200 shadow-xs mb-6 flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="w-full md:w-96">
          <input
            type="text"
            placeholder="Search student by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <span className="text-xs font-bold text-gray-500 whitespace-nowrap">Filter Status:</span>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-300 text-xs md:text-sm font-semibold bg-white text-darkNavy focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
          >
            <option value="all">All Students ({students.length})</option>
            <option value="top">Top Performers (80%+)</option>
            <option value="active">In Progress (1% - 79%)</option>
            <option value="needs_attention">Needs Attention (0% - 39%)</option>
          </select>
        </div>
      </div>

      {/* Main Student Roster Table */}
      {loading || debugMsg.startsWith('Error:') ? (
        <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-200">
          {!debugMsg.startsWith('Error:') && (
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          )}
          <p className="text-gray-600 font-medium text-center px-4 max-w-xl">
            {debugMsg}
          </p>
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-xs">
          <p className="text-lg font-bold text-gray-700 mb-1">No Matching Students Found</p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Try adjusting your search keyword or selected class level filter.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <th className="p-4">Student</th>
                  <th className="p-4">Class</th>
                  <th className="p-4">Average Progress</th>
                  <th className="p-4">Active Learning Focus</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {filteredStudents.map((student) => {
                  const activeSubjects = student.subjects?.filter(s => s.progress > 0) || [];
                  const initials = student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'ST';

                  return (
                    <tr key={student.id} className="hover:bg-gray-50/80 transition-colors">
                      
                      {/* Column 1: Student info */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-700 font-extrabold flex items-center justify-center text-xs flex-shrink-0">
                            {initials}
                          </div>
                          <div>
                            <p className="font-bold text-darkNavy leading-tight">
                              {student.name}
                            </p>
                            {student.email && (
                              <p className="text-xs text-gray-400 font-medium mt-0.5">
                                {student.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Column 2: Class */}
                      <td className="p-4">
                        <span className="font-bold text-xs px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 border border-gray-200 uppercase">
                          {student.classLevel}
                        </span>
                      </td>

                      {/* Column 3: Progress Bar & Score */}
                      <td className="p-4 min-w-[180px]">
                        <div className="flex items-center gap-3 mb-1.5">
                          <span className="font-black text-sm text-darkNavy w-10">
                            {student.progress}%
                          </span>
                          <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${getProgressBarColor(student.progress)}`}
                              style={{ width: `${Math.max(student.progress, 3)}%` }}
                            />
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-400 font-medium">
                          {student.progress === 0 
                            ? 'No lesson completed yet' 
                            : student.progress >= 80 
                              ? 'Mastering syllabus topics' 
                              : 'Actively progressing'}
                        </p>
                      </td>

                      {/* Column 4: Clean Subject Highlights (NO 15 grey pills!) */}
                      <td className="p-4 max-w-xs">
                        {activeSubjects.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {activeSubjects.slice(0, 3).map((sub) => (
                              <span 
                                key={sub.subjectId} 
                                className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-md text-[11px] font-bold whitespace-nowrap"
                              >
                                {sub.subjectName.split(' (')[0]}: {sub.progress}%
                              </span>
                            ))}
                            {activeSubjects.length > 3 && (
                              <span className="text-[11px] font-bold text-gray-500 self-center">
                                +{activeSubjects.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">
                            All enrolled subjects at 0%
                          </span>
                        )}
                      </td>

                      {/* Column 5: Status Badge */}
                      <td className="p-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-extrabold border ${getStatusBadge(student.status)}`}>
                          {student.status}
                        </span>
                      </td>

                      {/* Column 6: Action Button */}
                      <td className="p-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => setSelectedStudent(student)}
                          className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-all cursor-pointer shadow-2xs"
                        >
                          View Breakdown
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal: Full Subject Breakdown (No icons, human design) */}
      {selectedStudent && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl border border-gray-100 animate-fade-in max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-gray-100 pb-4 mb-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-md">
                  {selectedStudent.classLevel.toUpperCase()}
                </span>
                <h3 className="text-2xl font-black text-darkNavy mt-2">
                  {selectedStudent.name}
                </h3>
                {selectedStudent.email && (
                  <p className="text-xs text-gray-400 font-medium">
                    {selectedStudent.email}
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedStudent(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 font-bold text-sm flex items-center justify-center transition-colors cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Overall Progress Banner */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-indigo-900">
                  Overall Syllabus Completion
                </span>
                <span className="text-lg font-black text-indigo-700">
                  {selectedStudent.progress}%
                </span>
              </div>
              <div className="w-full h-3 bg-white/80 rounded-full overflow-hidden border border-indigo-200">
                <div 
                  className={`h-full rounded-full ${getProgressBarColor(selectedStudent.progress)}`}
                  style={{ width: `${Math.max(selectedStudent.progress, 4)}%` }}
                />
              </div>
            </div>

            {/* Subject List Breakdown */}
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
              Enrolled Subject Breakdown ({selectedStudent.subjects?.length || 0})
            </h4>

            <div className="space-y-3 mb-6">
              {selectedStudent.subjects && selectedStudent.subjects.length > 0 ? (
                selectedStudent.subjects.map((sub) => (
                  <div 
                    key={sub.subjectId} 
                    className="p-3.5 rounded-xl border border-gray-100 bg-gray-50/60 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-bold text-sm text-darkNavy">
                        {sub.subjectName.split(' (')[0]}
                      </p>
                      {sub.totalLessons > 0 ? (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {sub.completedLessons || 0} of {sub.totalLessons} lessons completed
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Syllabus active
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-black text-darkNavy">
                        {sub.progress}%
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 italic">No enrolled subjects found.</p>
              )}
            </div>

            {/* Modal Close Button */}
            <button
              onClick={() => setSelectedStudent(null)}
              className="w-full py-3 rounded-xl bg-darkNavy text-white font-bold text-sm hover:bg-slate-800 transition-colors cursor-pointer"
            >
              Close Breakdown
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default TeacherProgress;
